import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Trophy, Zap, Activity, Flame, Calendar, Smartphone, Check, ArrowRight, X, Download } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SKILL_LEVELS = [
  { id: "beginner", label: "Beginner", icon: Activity, color: "blue", desc: "New to structured training" },
  { id: "intermediate", label: "Intermediate", icon: Zap, color: "orange", desc: "Trained for 6+ months" },
  { id: "advanced", label: "Advanced", icon: Flame, color: "red", desc: "Trained 2+ years consistently" },
];

const OnboardingFlow = ({ open, onComplete, userName }) => {
  const [step, setStep] = useState(0);
  const [skillLevel, setSkillLevel] = useState(null);
  const [weeklyGoal, setWeeklyGoal] = useState(4);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Capture PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const submitOnboarding = async (skip = false) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      if (skip || !skillLevel) {
        await axios.post(`${API}/user/onboarding/skip`, {}, { headers });
      } else {
        await axios.post(
          `${API}/user/onboarding`,
          { skill_level: skillLevel, weekly_workout_goal: weeklyGoal },
          { headers }
        );
      }
      onComplete();
    } catch (e) {
      console.error("Onboarding save failed:", e);
      toast.error("Could not save your preferences. You can update them in your profile later.");
      onComplete();
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => setStep((s) => s + 1);

  const tryInstallPWA = async () => {
    if (!installPrompt) {
      toast.info("To install, use your browser's Add to Home Screen option.");
      nextStep();
      return;
    }
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") {
      toast.success("App installed!");
    }
    setInstallPrompt(null);
    nextStep();
  };

  // PROGRESS DOTS
  const TOTAL_STEPS = 5;
  const ProgressDots = () => (
    <div className="flex justify-center gap-2 mb-6" data-testid="onboarding-progress">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i === step ? "w-8 bg-orange-500" : i < step ? "w-4 bg-orange-700" : "w-4 bg-zinc-800"
          }`}
        />
      ))}
    </div>
  );

  return (
    <Dialog open={open} modal>
      <DialogContent
        className="max-w-lg bg-zinc-950 border-2 border-zinc-800 text-white p-0 [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="onboarding-modal"
      >
        <DialogTitle className="sr-only">Onboarding</DialogTitle>
        <div className="p-6 md:p-8 relative">
          {/* Skip button */}
          {step > 0 && step < TOTAL_STEPS - 1 && (
            <button
              onClick={() => submitOnboarding(true)}
              disabled={submitting}
              className="absolute top-4 right-4 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-300 font-mono"
              data-testid="onboarding-skip-btn"
            >
              Skip
            </button>
          )}

          <ProgressDots />

          <AnimatePresence mode="wait">
            {/* STEP 0 — Welcome */}
            {step === 0 && (
              <motion.div
                key="welcome"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center space-y-5"
              >
                <div className="w-20 h-20 mx-auto bg-orange-500/10 border-2 border-orange-500/30 flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-orange-500" />
                </div>
                <p className="text-xs font-mono uppercase tracking-[0.3em] text-orange-500">Welcome</p>
                <h2 className="font-heading font-black uppercase text-3xl md:text-4xl tracking-tighter leading-none">
                  Welcome to <br /> Pivot, <span className="text-orange-500">{userName?.split(" ")[0] || "Athlete"}.</span>
                </h2>
                <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                  You're about to start a 23-week journey designed to make you undeniable on the court. Let's customize it for you in <span className="text-white font-bold">60 seconds</span>.
                </p>
                <Button
                  onClick={nextStep}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-heading font-black uppercase tracking-wider text-base h-12 btn-tactical"
                  data-testid="onboarding-welcome-next-btn"
                >
                  Let's Go <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {/* STEP 1 — Skill Level */}
            {step === 1 && (
              <motion.div
                key="skill"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div className="text-center">
                  <p className="text-xs font-mono uppercase tracking-[0.3em] text-orange-500 mb-3">Step 1 of 4</p>
                  <h2 className="font-heading font-black uppercase text-2xl md:text-3xl tracking-tighter mb-2">
                    What's Your Level?
                  </h2>
                  <p className="text-sm text-zinc-400">We'll calibrate your starting point.</p>
                </div>
                <div className="space-y-2">
                  {SKILL_LEVELS.map((s) => {
                    const Icon = s.icon;
                    const isSelected = skillLevel === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setSkillLevel(s.id)}
                        className={`w-full flex items-center gap-4 p-4 border-2 transition-all text-left ${
                          isSelected
                            ? "bg-orange-500/10 border-orange-500"
                            : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                        }`}
                        data-testid={`onboarding-skill-${s.id}-btn`}
                      >
                        <div className={`w-10 h-10 flex items-center justify-center border ${
                          isSelected ? "bg-orange-500 border-orange-500 text-white" : "bg-zinc-950 border-zinc-700 text-zinc-400"
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-heading font-black uppercase text-base tracking-tight">{s.label}</p>
                          <p className="text-xs text-zinc-500">{s.desc}</p>
                        </div>
                        {isSelected && <Check className="w-5 h-5 text-orange-500" />}
                      </button>
                    );
                  })}
                </div>
                <Button
                  onClick={nextStep}
                  disabled={!skillLevel}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-heading font-black uppercase tracking-wider text-base h-12 btn-tactical disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="onboarding-skill-next-btn"
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {/* STEP 2 — Weekly Goal */}
            {step === 2 && (
              <motion.div
                key="goal"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div className="text-center">
                  <p className="text-xs font-mono uppercase tracking-[0.3em] text-orange-500 mb-3">Step 2 of 4</p>
                  <Calendar className="w-12 h-12 text-orange-500 mx-auto mb-3" />
                  <h2 className="font-heading font-black uppercase text-2xl md:text-3xl tracking-tighter mb-2">
                    How Many Days <br /> Per Week?
                  </h2>
                  <p className="text-sm text-zinc-400">Pick a realistic number you'll actually hit.</p>
                </div>

                <div className="grid grid-cols-5 gap-2">
                  {[3, 4, 5, 6, 7].map((n) => (
                    <button
                      key={n}
                      onClick={() => setWeeklyGoal(n)}
                      className={`h-16 flex flex-col items-center justify-center border-2 transition-all ${
                        weeklyGoal === n
                          ? "bg-orange-500/10 border-orange-500"
                          : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                      }`}
                      data-testid={`onboarding-goal-${n}-btn`}
                    >
                      <span className="font-heading font-black text-2xl">{n}</span>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">days</span>
                    </button>
                  ))}
                </div>

                <p className="text-xs text-center text-zinc-500 italic">
                  {weeklyGoal === 3 && "Consistent. The most underrated rate."}
                  {weeklyGoal === 4 && "Solid balance — recovery + reps."}
                  {weeklyGoal === 5 && "Serious. The standard for serious players."}
                  {weeklyGoal === 6 && "Aggressive. Watch your recovery."}
                  {weeklyGoal === 7 && "Beast mode. Recovery becomes critical."}
                </p>

                <Button
                  onClick={nextStep}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-heading font-black uppercase tracking-wider text-base h-12 btn-tactical"
                  data-testid="onboarding-goal-next-btn"
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {/* STEP 3 — Baseline Vertical Test prompt */}
            {step === 3 && (
              <motion.div
                key="baseline"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5 text-center"
              >
                <p className="text-xs font-mono uppercase tracking-[0.3em] text-orange-500 mb-1">Step 3 of 4</p>
                <Trophy className="w-14 h-14 text-orange-500 mx-auto" />
                <h2 className="font-heading font-black uppercase text-2xl md:text-3xl tracking-tighter">
                  Log Your <br /> Baseline Vertical.
                </h2>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Want to actually see progress? You need a starting point. Log your current vertical jump now — we'll track every inch you gain.
                </p>
                <div className="bg-orange-500/10 border border-orange-500/30 p-3 text-xs text-orange-200">
                  💡 You can manually enter it OR use the AI Camera Measure (Pro/Elite feature) from your Vertical Tracker page.
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={nextStep}
                    className="bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-zinc-300 py-3 font-heading font-black uppercase tracking-wider text-sm transition"
                    data-testid="onboarding-baseline-later-btn"
                  >
                    Later
                  </button>
                  <button
                    onClick={() => {
                      onComplete({ navigateTo: "/vertical" });
                    }}
                    className="bg-orange-600 hover:bg-orange-500 text-white py-3 font-heading font-black uppercase tracking-wider text-sm btn-tactical"
                    data-testid="onboarding-baseline-now-btn"
                  >
                    Log Now
                  </button>
                </div>
                <button
                  onClick={nextStep}
                  className="text-xs text-zinc-500 hover:text-zinc-300 underline uppercase tracking-widest font-mono"
                >
                  Continue
                </button>
              </motion.div>
            )}

            {/* STEP 4 — PWA Install */}
            {step === 4 && (
              <motion.div
                key="install"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5 text-center"
              >
                <p className="text-xs font-mono uppercase tracking-[0.3em] text-orange-500 mb-1">Step 4 of 4</p>
                <div className="relative inline-block">
                  <Smartphone className="w-14 h-14 text-orange-500 mx-auto" />
                  <Download className="w-6 h-6 text-orange-400 absolute -bottom-1 -right-1 bg-zinc-950 rounded-full p-1 border-2 border-orange-500" />
                </div>
                <h2 className="font-heading font-black uppercase text-2xl md:text-3xl tracking-tighter">
                  Put It On <br /> Your Phone.
                </h2>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Install Pivot Performance as a real app on your phone. Works offline. No app store needed.
                </p>
                <div className="bg-zinc-900 border border-zinc-800 p-3 text-xs text-zinc-400 text-left space-y-1">
                  <p className="font-bold uppercase tracking-widest text-zinc-300 text-[10px] mb-1">How to install:</p>
                  <p><span className="text-orange-500">iPhone:</span> Tap Share → "Add to Home Screen"</p>
                  <p><span className="text-orange-500">Android:</span> Tap menu (⋮) → "Install App"</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => submitOnboarding(false)}
                    disabled={submitting}
                    className="bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-zinc-300 py-3 font-heading font-black uppercase tracking-wider text-sm transition"
                    data-testid="onboarding-install-later-btn"
                  >
                    Maybe Later
                  </button>
                  <button
                    onClick={() => {
                      tryInstallPWA();
                      submitOnboarding(false);
                    }}
                    disabled={submitting}
                    className="bg-orange-600 hover:bg-orange-500 text-white py-3 font-heading font-black uppercase tracking-wider text-sm btn-tactical"
                    data-testid="onboarding-install-now-btn"
                  >
                    Install
                  </button>
                </div>
                <button
                  onClick={() => submitOnboarding(false)}
                  disabled={submitting}
                  className="text-xs text-zinc-500 hover:text-zinc-300 underline uppercase tracking-widest font-mono"
                  data-testid="onboarding-finish-btn"
                >
                  Finish & Go to Dashboard →
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingFlow;
